import { ReactNode, useState } from "react";
import { FaMinus, FaPlus } from "react-icons/fa6";

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQProps {
  items: FAQItem[];
  title?: ReactNode;
  subtitle?: ReactNode;
  label?: ReactNode;
  containerClassName?: string;
  maxWidth?: string;
  idPrefix?: string;
  titleClassName?: string;
  subtitleClassName?: string;
  labelClassName?: string;
}

const FAQ = ({
  items,
  title = "Frequently asked questions",
  subtitle = "Find quick answers to common questions about our services.",
  label = "FAQ",
  containerClassName = "bg-white py-16 sm:py-20",
  maxWidth = "max-w-4xl",
  idPrefix = "faq",
  titleClassName = "mt-4 text-4xl font-light font-lato tracking-tight text-black-1a sm:text-5xl leading-tight",
  subtitleClassName = "mx-auto mt-4 max-w-[65ch] text-lg leading-8 text-dark-6b sm:text-lg",
  labelClassName = "inline-flex rounded-full border border-blue-ea bg-white px-4 py-2 text-xs font-semibold uppercase tracking-widest text-blue-00",
}: FAQProps) => {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <section className={containerClassName}>
      <div className={`mx-auto ${maxWidth} px-6 lg:px-8`}>
        <div className="text-center">
          <p className={labelClassName}>{label}</p>
          <h2 className={titleClassName}>{title}</h2>
          <p className={subtitleClassName}>{subtitle}</p>
        </div>

        <div className="mt-10 space-y-4">
          {items.map((item, index) => {
            const isOpen = index === openIndex;

            return (
              <article
                key={item.question}
                className="rounded-2xl border border-grey-eb bg-white shadow-card transition-all duration-300 hover:border-blue-ea hover:shadow-card-md"
              >
                <button
                  type="button"
                  onClick={() => setOpenIndex(isOpen ? -1 : index)}
                  className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left transition-all duration-300"
                  aria-expanded={isOpen}
                  aria-controls={`${idPrefix}-panel-${index}`}
                  id={`${idPrefix}-trigger-${index}`}
                >
                  <span className="text-base text-black-1a">{item.question}</span>
                  <span className="flex h-8 w-8 items-center justify-center rounded-full border border-blue-ea bg-blue-ea text-blue-00 transition-all duration-300">
                    {isOpen ? <FaMinus size={12} /> : <FaPlus size={12} />}
                  </span>
                </button>

                <div
                  id={`${idPrefix}-panel-${index}`}
                  role="region"
                  aria-labelledby={`${idPrefix}-trigger-${index}`}
                  className={`grid transition-all duration-300 ease-out ${
                    isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                  }`}
                >
                  <div className="overflow-hidden">
                    <p className="px-6 pb-6 leading-7 text-dark-6b">
                      {item.answer}
                    </p>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Legacy export for About page backwards compatibility
const AboutFAQ = () => {
  const aboutFaqItems: FAQItem[] = [
    {
      question: "What does SeamlessVisa do?",
      answer:
        "SeamlessVisa is a U.S. immigration platform that combines expert guidance and AI-powered tools to help users reduce avoidable mistakes and prepare stronger applications.",
    },
    {
      question: "Who is SeamlessVisa built for?",
      answer:
        "We support individual applicants, students, and businesses that need structured immigration support, review workflows, and interview preparation.",
    },
    {
      question: "How does FiBi fit into the experience?",
      answer:
        "FiBi is our AI assistant for interview preparation and support workflows. It helps users practice, identify weak spots, and build confidence before key immigration steps.",
    },
    {
      question: "Do I still get human support?",
      answer:
        "Yes. SeamlessVisa blends software and human expertise so users can move faster while still getting trusted support where it matters most.",
    },
  ];

  return (
    <FAQ
      items={aboutFaqItems}
      title={
        <>
          Frequently Asked{" "}
          <span className="text-blue-00 italic mx-2">Questions</span>
        </>
      }
      subtitle="Quick answers about who we are, what we build, and how we support your immigration journey."
      containerClassName="bg-white py-16 sm:py-20"
    />
  );
};

export default AboutFAQ;
export { FAQ };
